/**
 * Quote breakdown — per-season day rows, route fee, insurance, add-ons,
 * refundable deposit (+ deposit-payment-method hint), and the final total
 * with a "we'll charge X" note. Extracted from `BookingWidget.jsx`.
 *
 * Iter 67t: split-commission preview re-skinned to match the Iter-67 colour
 * language. "Pay online now" lives on a sky→indigo gradient panel (matches the
 * Reserve CTA gradient); "Pay at pickup" sits on a tinted indigo half so the
 * two halves read as one continuous statement. Subscription vendors (whose
 * commission_amount is 0) get a single emerald "Pay at pickup" panel instead.
 */
import { useTranslation } from "react-i18next";
import Money from "@/components/Money";
// Iter 67en+++++ — SEASON_META no longer rendered; we now show
// just "rate × N days" (no off-peak / peak / shoulder label).
import { useCurrency } from "@/lib/currency";
import { CreditCard, Wallet, ShieldCheck, Plane } from "lucide-react";

function _depositMethodHintKey(method) {
  if (method === "cash") return "bookingWidget.depositMethodCash";
  if (method === "card") return "bookingWidget.depositMethodCard";
  if (method === "cash_or_card") return "bookingWidget.depositMethodCashOrCard";
  return null;
}

export function BookingPriceBreakdown({ car, quote }) {
  const { t } = useTranslation();
  const { currency, format } = useCurrency();
  if (!quote) return null;

  const total = quote.total ?? 0;
  const depositHintKey = _depositMethodHintKey(car.deposit_payment_method);
  const commissionPct = Math.round(Number(quote.commission_rate || 0) * 100);

  return (
    <div className="py-4 border-y border-border space-y-2 text-sm" data-testid="booking-breakdown">
      {Object.entries(quote.breakdown || {}).map(([season, b]) => {
        // Iter 67en+++++ — Render only "rate × N day(s)"; drop the rate
        // type label (custom_rate / off-peak / peak / shoulder), it's
        // jargon that confuses renters more than it informs them.
        return (
          <div key={season} className="flex justify-between">
            <span className="text-neutral-600">{format(b.rate)} × {b.days} day{b.days !== 1 ? "s" : ""}</span>
            <Money amount={b.subtotal} />
          </div>
        );
      })}

      {quote.route_fee > 0 && (
        <div className="flex justify-between" data-testid="booking-route-fee">
          <span className="text-neutral-600">
            {quote.route_fee_source === "matrix" && quote.route_distance_km > 0
              ? <>Drop-off fee · {quote.route_distance_km} km × <Money amount={quote.per_km_rate} /></>
              : "One-way fee"}
          </span>
          <Money amount={quote.route_fee} />
        </div>
      )}

      {Number(quote.meet_greet_fee) > 0 && (
        <div className="flex justify-between" data-testid="booking-meet-greet-fee">
          <span className="text-neutral-600 inline-flex items-center gap-1.5">
            <Plane className="h-3.5 w-3.5 text-sky-600" />
            Meet & Greet at airport
          </span>
          <Money amount={quote.meet_greet_fee} />
        </div>
      )}

      {quote.insurance_fee > 0 && (
        <div className="flex justify-between" data-testid="booking-insurance-fee">
          <span className="text-neutral-600 inline-flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
            Insurance · {quote.insurance_plan_name} · <Money amount={quote.insurance_price_per_day} /> × {quote.days} days
          </span>
          <Money amount={quote.insurance_fee} />
        </div>
      )}

      {Array.isArray(quote.add_ons) && quote.add_ons.map((a) => (
        <div key={a.id} className="flex justify-between" data-testid={`booking-add-on-fee-${a.id}`}>
          <span className="text-neutral-600">
            {a.name}
            {a.pricing === "per_day" && quote.days > 0 ? <> · <Money amount={a.price} /> × {quote.days} days</> : <> · one-time</>}
          </span>
          <Money amount={a.fee} />
        </div>
      ))}

      {quote.deposit > 0 && (
        <div className="flex justify-between" data-testid="booking-deposit">
          <span className="text-neutral-600">{t("bookingWidget.deposit")}</span>
          <Money amount={quote.deposit} />
        </div>
      )}
      {quote.deposit > 0 && depositHintKey && (
        <div className="text-[11px] text-neutral-500" data-testid="deposit-method-hint">
          {t(depositHintKey)}
        </div>
      )}

      <div className="flex justify-between font-semibold pt-2 border-t border-border">
        <span>{t("common.total")}</span>
        <Money amount={total} />
      </div>

      {/* Iter 67ap: split-commission preview — sky→indigo gradient top half
          ("Pay online now") + indigo-tinted bottom half ("Pay at pickup"), with
          a coloured medallion on each row so renters can read the funnel at a
          glance. Iter 67ap added an explicit 2-step header + numbered badges
          on each half so the payment model reads as one continuous statement
          rather than two disconnected amounts. */}
      {Number(quote.commission_amount) > 0 && (
        <div
          className="mt-3 rounded-2xl border border-indigo-100 overflow-hidden shadow-sm"
          data-testid="booking-split-preview"
        >
          <div className="bg-white px-3.5 py-2 border-b border-indigo-100 flex items-center justify-between gap-2" data-testid="booking-split-header">
            <div className="text-[10px] uppercase tracking-wider font-bold text-indigo-900">Your payment in 2 steps</div>
            <div className="text-[10px] text-neutral-500">
              Total <Money amount={total} className="font-semibold text-neutral-700" />
            </div>
          </div>
          <div className="bg-gradient-to-br from-sky-500 via-indigo-600 to-violet-600 text-white px-3.5 py-3" data-testid="booking-split-online">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="h-8 w-8 rounded-xl bg-white/15 backdrop-blur-sm ring-1 ring-white/20 flex items-center justify-center shrink-0 relative">
                  <CreditCard className="h-4 w-4 text-white" />
                  <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-white text-indigo-700 text-[9px] font-bold flex items-center justify-center ring-1 ring-indigo-200">1</span>
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-white/85">Step 1 · Pay online today — secures your booking</div>
                  <div className="text-[10.5px] text-white/85 truncate">
                    Platform service fee ({commissionPct}%)
                  </div>
                </div>
              </div>
              <Money amount={quote.amount_paid_online} className="font-display text-xl font-semibold text-white shrink-0" testId="booking-split-online-amount" />
            </div>
          </div>
          <div className="bg-indigo-50/60 px-3.5 py-3" data-testid="booking-split-pickup">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="h-8 w-8 rounded-xl bg-amber-100 ring-1 ring-amber-200 flex items-center justify-center shrink-0 relative">
                  <Wallet className="h-4 w-4 text-amber-700" />
                  <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-amber-600 text-white text-[9px] font-bold flex items-center justify-center ring-1 ring-amber-200">2</span>
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-700">Step 2 · Pay at pickup</div>
                  <div className="text-[10.5px] text-neutral-600 truncate">
                    Rental balance{quote.deposit > 0 ? " + refundable deposit" : ""} · directly to the host
                  </div>
                </div>
              </div>
              <Money amount={quote.amount_due_at_pickup} className="font-display text-lg font-semibold text-neutral-900 shrink-0" testId="booking-split-pickup-amount" />
            </div>
          </div>
        </div>
      )}

      {/* Subscription-vendor flow — platform takes nothing online. */}
      {Number(quote.commission_amount) === 0 && (
        <div
          className="mt-3 rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-teal-50/60 px-3.5 py-3.5"
          data-testid="booking-pay-at-pickup-only"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="h-9 w-9 rounded-xl bg-emerald-100 ring-1 ring-emerald-200 flex items-center justify-center shrink-0">
                <Wallet className="h-4.5 w-4.5 text-emerald-700" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-800">Pay at pickup</div>
                <div className="text-[10.5px] text-neutral-700">
                  Nothing charged online today — settle the full rental{quote.deposit > 0 ? " + deposit" : ""} with the host.
                </div>
              </div>
            </div>
            <Money amount={total} className="font-display text-xl font-semibold text-emerald-700 shrink-0" testId="booking-pay-at-pickup-only-amount" />
          </div>
        </div>
      )}

      <div className="text-[11px] text-neutral-400 pt-1" data-testid="booking-charge-note">
        {Number(quote.commission_amount) > 0
          ? (currency === "EUR"
              ? `We'll charge you ${format(quote.amount_paid_online)} today.`
              : `We'll charge you €${Number(quote.amount_paid_online).toFixed(2)} today (≈ ${format(quote.amount_paid_online)}).`)
          : "Nothing is charged online — confirm to send the booking to the host."}
      </div>
    </div>
  );
}

export default BookingPriceBreakdown;
